/**
 * Core Game Engine
 * 
 * Main game logic ported from louo.as
 * Handles map management, resource calculations, building placement, and optimization.
 */

import {
    WALL, TOWN_HALL, DEEP_WATER, WATER, LAND,
    WOOD, CUTTER, SAWMILL, CUTTER_OLD,
    STONE, QUARRY, STONEMASON, QUARRY_OLD,
    IRON, MINE, FOUNDRY, MINE_OLD,
    FOOD, OPEN, FARM, MILL, LAKE, FARM_OLD,
    GOLD, MANOR, BAZAAR, WHARF,
    CASA, CELLARIUM, CACHE,
    SHIPYARD, BARRACKS, CASTLE, CITY_GUARD_HOUSE,
    MOONGLOW, STABLES, TEMPLE, TRAINING_GROUND, WORKSHOP,
    ANY, IS_WATER, IS_LAND, TERRAIN_TYPE,
    DUMMY, BASIC_GATHERER, REFINER,
    X, x, _, o, O,
    ZONE, LAND_MAP, WATER_MAP,
    isBuilding, isResource, isRealResource, isMilitary
} from './Constants.js';

import { TileValue } from './TileValue.js';
import { UndoRedo } from './UndoRedo.js';
import { clone } from '../utils/Clone.js';
import { deepCompare } from '../utils/DeepCompare.js';
import {
    getGathererProduction,
    getFarmProduction,
    getEstateProduction,
    getCottageBonus,
    getRefineryBonus,
    getHarborBonus,
    getMarketBonus,
    getKeepBuildingSlots,
    BASILICA_WOOD_PRODUCTION
} from './BuildingData.js';

export class GameEngine {
    constructor() {
        // Map arrays (21x21)
        this.map = this.createMapFromTemplate(LAND_MAP);
        this.landMap = this.createMapFromTemplate(LAND_MAP);
        this.waterMap = this.createMapFromTemplate(WATER_MAP);
        this.origMap = this.createMapFromTemplate(LAND_MAP);
        
        // Lock system (21x21) - tracks which tiles are locked
        this.locks = this.createEmptyMap();
        
        // Wheat fields (21x21) - tracks which tiles have wheat fields (for farm bonuses)
        this.wheatFields = this.createEmptyMap();
        
        // Current terrain mode
        this.water = false;
        
        // Resource totals
        this.wood_tot = 0;
        this.stone_tot = 0;
        this.iron_tot = 0;
        this.food_tot = 0;
        this.gold_tot = 0;
        
        // Storage
        this.wood_storage = 0;
        this.stone_storage = 0;
        this.iron_storage = 0;
        this.food_storage = 0;
        
        // Hidden resources (caches)
        this.wood_hidden = 0;
        this.stone_hidden = 0;
        this.iron_hidden = 0;
        this.food_hidden = 0;
        
        // Building counts
        this.building_tot = 0;
        this.casa_tot = 0;
        this.producer_tot = 0;
        this.refiner_tot = 0;
        
        // Military buildings
        this.guard_tot = 0;
        this.training_ground_tot = 0;
        this.stable_tot = 0;
        this.moonglow_tot = 0;
        this.workshop_tot = 0;
        this.shipyard_tot = 0;
        this.temple_tot = 0;
        
        // Military rates
        this.guard_rate = 0;
        this.training_ground_rate = 0;
        this.stable_rate = 0;
        this.moonglow_rate = 0;
        this.workshop_rate = 0;
        this.shipyard_rate = 0;
        this.temple_rate = 0;
        
        // Other totals
        this.cart_tot = 0;
        this.ship_tot = 0;
        this.troop_capacity_tot = 0;
        this.building_rate = 1;
        
        // Optimization weights
        this.wood_weight = 1.0;
        this.stone_weight = 1.0;
        this.iron_weight = 1.0;
        this.food_weight = 0.5;
        this.gold_weight = 1.3;
        
        // Constants for calculations
        this.storage_base = 175000;
        this.storage_per_cellarium = 200000;
        this.cache_capacity = 15000;
        
        // Resource bonuses arrays
        // Each resource node gives 45% bonus (135 at base 300)
        // All nodes give the same bonus, not 50% first then 40% subsequent
        this.resource_bonuses = [
            0.0, 135.0, 135*2, 135*3, 135*4,
            135*5, 135*6, 135*7, 135*8
        ];
        
        this.old_resource_bonuses = [
            0.0, 75.0, 75.0*2, 75.0*3, 75.0*4,
            75.0*5, 75.0*6, 75.0*7, 75.0*8
        ];
        
        this.old_food_resource_bonuses = [
            0.0, 100.0, 100.0*2, 100.0*3, 100.0*4,
            100.0*5, 100.0*6, 100.0*7, 100.0*8
        ];
        
        // Multipliers
        this.REFINER_MULTIPLIER = 1.7500001;
        this.CASA_MULTIPLIER = 0.3000001;
        this.OLD_COTTAGE_MULTIPLIER = 0.5000001;
        this.WHARF_MULTIPLIER = 0.500001;
        this.BAZAAR_MULTIPLIER = 0.200001;
        
        // Tilesets for optimization
        this.tilesets = {
            [WOOD]: [CUTTER, SAWMILL, CASA, CELLARIUM],
            [STONE]: [QUARRY, STONEMASON, CASA, CELLARIUM],
            [IRON]: [MINE, FOUNDRY, CASA, CELLARIUM],
            [FOOD]: [FARM, MILL, OPEN, CASA, CELLARIUM],
            [GOLD]: [WHARF, BAZAAR, MANOR],
            [CASA]: [CASA],
            [ANY]: [CUTTER, QUARRY, MINE, FARM, SAWMILL, STONEMASON, FOUNDRY, MILL, OPEN, CASA, WHARF, WATER, BAZAAR, MANOR, CELLARIUM, CACHE, BARRACKS],
            [BASIC_GATHERER]: [CUTTER, QUARRY, MINE, FARM, MANOR],
            [REFINER]: [SAWMILL, STONEMASON, FOUNDRY, MILL, BAZAAR, CASA, CELLARIUM]
        };
        
        // Key bindings for keyboard shortcuts
        // Resources use share string characters: '.' for WOOD, ':' for STONE, ',' for IRON, ';' for LAKE
        this.keyBindings = {
            '.': [WOOD, 'resource_wood', "Forest", 'A', '.'],
            ':': [STONE, 'resource_stone', "Stone", 'B', ':'],
            ',': [IRON, 'resource_iron', "Iron", 'C', ','],
            ';': [LAKE, 'resource_lake', "Lake", 'D', ';'],
            
            'f': [CUTTER, 'building_forester', "Lumber Mill", '2', '2'],
            's': [QUARRY, 'building_quarry', "Quarry", '3', '3'],
            'i': [MINE, 'building_mine', "Iron Mine", '4', '4'],
            'a': [FARM, 'building_farm', "Manor", '5', '1'],
            
            'l': [SAWMILL, 'building_sawmill', "Sawmill", 'K', 'L'],
            'h': [STONEMASON, 'building_stonemason', "Mason's Workshop", 'L', 'A'],
            'z': [FOUNDRY, 'building_foundry', "Forge", 'M', 'D'],
            'g': [MILL, 'building_mill', "Mill", 'N', 'M'],
            
            'o': [WHARF, 'building_wharf', "Wharf", 'T', 'R'],
            'v': [MANOR, 'building_manor', "Manor", 'E', 'U'],
            'm': [BAZAAR, 'building_bazaar', "Bazaar", 'J', 'P'],
            
            'c': [CASA, 'building_casa', "Casa", 'O', 'C'],
            'r': [CELLARIUM, 'building_cellarium', "Cellarium", 'Z', 'S'],
            'q': [CACHE, 'building_cache', "Cache", '1', 'H'],
            
            'b': [BARRACKS, 'building_barracks', "Barracks", 'P', 'B'],
            'u': [CITY_GUARD_HOUSE, 'building_guard_house', "Guard House", 'S', 'K'],
            't': [TRAINING_GROUND, 'building_training_ground', "Training Grounds", 'Q', 'G'],
            'y': [TEMPLE, 'building_temple', "Academy", 'W', 'Z'],
            'k': [WORKSHOP, 'building_workshop', "Blacksmith", 'V', 'Y'],
            
            'p': [SHIPYARD, 'building_shipyard', "Shipyard", 'Y', 'V'],
            'e': [STABLES, 'building_stable', "Stables", 'U', 'E'],
            'x': [CASTLE, 'building_castle', "Castle", 'X', 'X'],
            'w': [MOONGLOW, 'building_moonglow', "Sorcerer's Tower", 'R', 'J'],
            
            'water': [WATER, 'tile_empty', "Water", '0', '_'],
            '<space>': [DUMMY, 'locked', "Lock / Unlock", '!!!', '!!!'],
            '<shift>': [DUMMY, 'summary_grid', "Summary Grid", '!!!', '!!!'],
            '`, 0, or <del>': [DUMMY, 'delete', "Remove Tile", '!!!', '!!!'],
            'open': [OPEN, 'tile_empty', "Open", '0', '-'],
            
            'wall': [WALL, 'tile_empty', "Wall", '', '#'],
            'deepwater': [DEEP_WATER, 'tile_empty', "Deep water", '0', '#'],
            'th': [TOWN_HALL, 'tile_empty', "Keep", '0', 'T']
        };
        
        // History for undo/redo
        this.undo_stack = [];
        this.redo_stack = [];
        
        // Last loaded string for reset
        this.last_load_string = null;
        
        // Message display
        this.message = "";
        
        // Building levels (default to 10)
        this.buildingLevel = 10;
        this.basilicaLevel = 10;
        this.wallLevel = 10;
        
        // UI references (set by MainApp)
        this.app = null;
        
        // Initialize undo/redo after constructor
        this.undoRedo = new UndoRedo(this);
        
        // Evaluation cache for optimization performance
        this.evalCache = null;
        this.cacheValid = false;
    }
    
    /**
     * Create an empty 21x21 grid
     */
    createEmptyMap() {
        const map = [];
        for (let i = 0; i < 21; i++) {
            map[i] = new Array(21).fill(null);
        }
        return map;
    }
    
    /**
     * Create map from template (deep copy)
     */
    createMapFromTemplate(template) {
        return clone(template);
    }
    
    /**
     * Copy current map to original map
     */
    copyMapToOrigMap() {
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                this.origMap[i][j] = this.map[i][j];
            }
        }
    }
    
    /**
     * Stash current map state (for optimization)
     */
    stashMap() {
        return clone(this.map);
    }
    
    /**
     * Get tile type by key binding
     */
    getTileByKey(ch, i, j) {
        if (!this.keyBindings.hasOwnProperty(ch)) {
            return this.water ? this.waterMap[i][j] : this.landMap[i][j];
        }
        return this.keyBindings[ch][0];
    }
    
    /**
     * Convert tile to character
     */
    tileToChar(tile) {
        for (const k in this.keyBindings) {
            if (this.keyBindings[k][0] === tile) {
                return k;
            }
        }
        console.warn(`Invalid tile: ${tile}`);
        return ' ';
    }
    
    /**
     * Count buildings of a specific type
     */
    countBuildings(buildingType) {
        let count = 0;
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === buildingType) {
                    count++;
                }
            }
        }
        return count;
    }
    
    /**
     * Count all buildings
     */
    countAllBuildings() {
        let count = 0;
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                const tile = this.map[i][j];
                if ((tile & 0x000f) && tile > 0x1000 && tile !== LAKE) {
                    count++;
                }
            }
        }
        return count;
    }
    
    /**
     * Count tiles around a position
     */
    countAround(tileType, i, j) {
        // Check bounds for each neighbor position to handle edge cases
        let count = 0;
        
        // Check all 8 neighbors, only counting if they're within bounds
        for (let di = -1; di <= 1; di++) {
            for (let dj = -1; dj <= 1; dj++) {
                if (di === 0 && dj === 0) continue; // Skip center tile
                const ni = i + di;
                const nj = j + dj;
                if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                    if (this.map[ni][nj] === tileType) {
                        count++;
                    }
                }
            }
        }
        
        return count;
    }
    
    /**
     * Count around with mask
     */
    countAroundMask(mask, eq, i, j) {
        // Check bounds for each neighbor position to handle edge cases
        let count = 0;
        
        // Check all 8 neighbors, only counting if they're within bounds
        for (let di = -1; di <= 1; di++) {
            for (let dj = -1; dj <= 1; dj++) {
                if (di === 0 && dj === 0) continue; // Skip center tile
                const ni = i + di;
                const nj = j + dj;
                if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                    if ((mask & this.map[ni][nj]) === eq) {
                        count++;
                    }
                }
            }
        }
        
        return count;
    }
    
    /**
     * Count tiles of same type in a 5x5 area (used for refinery spacing)
     */
    countAround2(tileType, i, j) {
        let count = 0;
        
        // Check 5x5 area (from -2 to +2 in both directions)
        for (let di = -2; di <= 2; di++) {
            for (let dj = -2; dj <= 2; dj++) {
                const ni = i + di;
                const nj = j + dj;
                // Only count if within bounds (0-20)
                if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                    if (this.map[ni][nj] === tileType) {
                        count++;
                    }
                }
            }
        }
        
        return count;
    }
    
    /**
     * Check if a tile is locked
     */
    isLocked(i, j) {
        if (i < 0 || i >= 21 || j < 0 || j >= 21) return false;
        return this.locks[i][j] === true;
    }
    
    /**
     * Set lock state
     */
    setLocked(i, j, locked) {
        if (i < 0 || i >= 21 || j < 0 || j >= 21) return;
        this.locks[i][j] = locked;
    }
    
    /**
     * Toggle lock state
     */
    toggleLocked(i, j) {
        this.setLocked(i, j, !this.isLocked(i, j));
    }
    
    /**
     * Reset all locks
     */
    resetLocks() {
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                this.locks[i][j] = false;
            }
        }
    }
    
    /**
     * Lock all buildings
     */
    lockBuildings() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (isBuilding(this.map[i][j])) {
                    this.locks[i][j] = true;
                }
            }
        }
    }
    
    /**
     * Lock all resources
     */
    lockResources() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                const tile = this.map[i][j];
                if (tile === WOOD || tile === IRON || tile === STONE || tile === LAKE) {
                    this.locks[i][j] = true;
                }
            }
        }
    }
    
    /**
     * Check if any tiles in array are locked
     */
    anyLocked(arr) {
        for (let k = 0; k < arr.length; k++) {
            if (this.isLocked(arr[k][0], arr[k][1])) {
                return true;
            }
        }
        return false;
    }
    
    /**
     * Check if all tiles in array are locked
     */
    allLocked(arr) {
        for (let k = 0; k < arr.length; k++) {
            if (!this.isLocked(arr[k][0], arr[k][1])) {
                return false;
            }
        }
        return true;
    }
    
    /**
     * Filter out locked tiles from array
     */
    filterLockedTiles(arr) {
        const ret = [];
        for (let k = 0; k < arr.length; k++) {
            if (!this.isLocked(arr[k][0], arr[k][1])) {
                ret.push(arr[k]);
            }
        }
        return ret;
    }
    
    /**
     * Place building by character code
     */
    placeByChar(ch, i, j, saveUndo = true) {
        if (i < 0 || i >= 21 || j < 0 || j >= 21) return;
        
        // Save state before modification (unless explicitly disabled)
        if (saveUndo && this.undoRedo) {
            this.undoRedo.saveState();
        }
        
        // Handle delete/remove
        if (ch === '`' || ch === '0' || ch === '<del>') {
            const baseMap = this.water ? this.waterMap : this.landMap;
            const oldTile = this.map[i][j];
            this.map[i][j] = baseMap[i][j];
            
            // Remove wheat field from this spot
            this.wheatFields[i][j] = null;
            
            // If removing a farm, remove wheat fields around it
            if (oldTile === FARM || oldTile === FARM_OLD) {
                this.removeWheatFieldsAroundFarm(i, j);
            }
            
            this.updateTotals();
            return;
        }
        
        // Get tile type for this key
        const tile = this.getTileByKey(ch, i, j);
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        // DEEP_WATER tiles cannot have any buildings placed on them (including WHARF/SHIPYARD)
        if (baseMap[i][j] === DEEP_WATER) {
            this.message = "Cannot place buildings on deep water";
            return;
        }
        
        // Check if trying to place on a wall square
        if (baseMap[i][j] === WALL) {
            this.message = "Cannot place building on the wall";
            return;
        }
        
        // Check if trying to place on the basilica (Town Hall)
        if (baseMap[i][j] === TOWN_HALL) {
            this.message = "Cannot place building on the basilica";
            return;
        }
        
        // Check terrain compatibility using TERRAIN_TYPE mask
        // This ensures water buildings (WHARF, SHIPYARD) can only be placed on water
        // and land buildings can only be placed on land
        if ((baseMap[i][j] & TERRAIN_TYPE) === (tile & TERRAIN_TYPE)) {
            const oldTile = this.map[i][j];
            this.map[i][j] = tile;
            
            // Remove wheat field from this spot when placing any building
            this.wheatFields[i][j] = null;
            
            // If placing a farm, create wheat fields in adjacent OPEN spots
            if (tile === FARM || tile === FARM_OLD) {
                this.createWheatFieldsAroundFarm(i, j);
            }
            
            // If removing a farm, remove wheat fields around it
            if ((oldTile === FARM || oldTile === FARM_OLD) && (tile !== FARM && tile !== FARM_OLD)) {
                this.removeWheatFieldsAroundFarm(i, j);
            }
            
            this.updateTotals();
        } else {
            // Terrain mismatch - don't place the building
            this.message = `Cannot place ${ch === 'o' ? 'Harbor' : ch === 'p' ? 'Shipyard' : 'building'} on ${(baseMap[i][j] & IS_WATER) ? 'land' : 'water'}`;
        }
    }
    
    /**
     * Undo last action
     */
    undo() {
        if (this.undoRedo && this.undoRedo.undo()) {
            return true;
        }
        return false;
    }
    
    /**
     * Redo last undone action
     */
    redo() {
        if (this.undoRedo && this.undoRedo.redo()) {
            return true;
        }
        return false;
    }
    
    /**
     * Check if undo is available
     */
    canUndo() {
        return this.undoRedo ? this.undoRedo.canUndo() : false;
    }
    
    /**
     * Check if redo is available
     */
    canRedo() {
        return this.undoRedo ? this.undoRedo.canRedo() : false;
    }
    
    /**
     * Update water/land tiles based on current mode
     */
    updateWaterTiles(water) {
        this.water = water;
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                if (water) {
                    if (this.map[i][j] === _ && (this.waterMap[i][j] === o || this.waterMap[i][j] === O)) {
                        this.map[i][j] = this.waterMap[i][j];
                    }
                    if (this.origMap[i][j] === _ && (this.waterMap[i][j] === o || this.waterMap[i][j] === O)) {
                        this.origMap[i][j] = this.waterMap[i][j];
                    }
                } else {
                    if (this.map[i][j] === o || this.map[i][j] === O) {
                        this.map[i][j] = this.landMap[i][j];
                    }
                    if (this.origMap[i][j] === o || this.origMap[i][j] === O) {
                        this.origMap[i][j] = this.landMap[i][j];
                    }
                }
            }
        }
    }
    
    /**
     * Check if a position has a wheat field
     * @param {number} i - Row position
     * @param {number} j - Column position
     * @returns {boolean} True if this position has a wheat field
     */
    hasWheatField(i, j) {
        if (i < 0 || i >= 21 || j < 0 || j >= 21) return false;
        const field = this.wheatFields[i][j];
        return Array.isArray(field) && field.length > 0;
    }
    
    /**
     * Create wheat fields around a farm (3x3 square)
     * Wheat fields are created on all adjacent buildable spots (less restrictive)
     * @param {number} i - Farm row position
     * @param {number} j - Farm column position
     */
    createWheatFieldsAroundFarm(i, j) {
        const baseMap = this.water ? this.waterMap : this.landMap;
        const farmKey = `${i},${j}`;
        
        // Check all 8 adjacent positions
        for (let di = -1; di <= 1; di++) {
            for (let dj = -1; dj <= 1; dj++) {
                if (di === 0 && dj === 0) continue; // Skip the farm itself
                
                const ni = i + di;
                const nj = j + dj;
                
                // Bounds check
                if (ni < 0 || ni >= 21 || nj < 0 || nj >= 21) continue;
                
                // Skip walls and deep water (but allow other tiles)
                const baseTile = baseMap[ni][nj];
                if (baseTile === WALL || baseTile === DEEP_WATER) continue;
                
                // Create wheat field on OPEN spots (less restrictive - no locked check, no water restriction)
                // Allow on OPEN spots regardless of lock status
                const currentTile = this.map[ni][nj];
                if (currentTile === OPEN) {
                    // Initialize array if needed
                    if (!Array.isArray(this.wheatFields[ni][nj])) {
                        this.wheatFields[ni][nj] = [];
                    }
                    // Add this farm to the list if not already present
                    if (!this.wheatFields[ni][nj].includes(farmKey)) {
                        this.wheatFields[ni][nj].push(farmKey);
                    }
                }
            }
        }
    }
    
    /**
     * Remove wheat fields around a farm when the farm is removed
     * Removes this farm from the tracking array, and removes the field if no other farms created it
     * @param {number} i - Farm row position
     * @param {number} j - Farm column position
     */
    removeWheatFieldsAroundFarm(i, j) {
        const farmKey = `${i},${j}`;
        
        // Check all 8 adjacent positions
        for (let di = -1; di <= 1; di++) {
            for (let dj = -1; dj <= 1; dj++) {
                if (di === 0 && dj === 0) continue; // Skip the farm itself
                
                const ni = i + di;
                const nj = j + dj;
                
                // Bounds check
                if (ni < 0 || ni >= 21 || nj < 0 || nj >= 21) continue;
                
                // Only process if there's a wheat field here
                const field = this.wheatFields[ni][nj];
                if (!Array.isArray(field) || field.length === 0) continue;
                
                // Remove this farm from the tracking array
                const index = field.indexOf(farmKey);
                if (index !== -1) {
                    field.splice(index, 1);
                }
                
                // If no farms are tracking this field anymore, clear it
                if (field.length === 0) {
                    this.wheatFields[ni][nj] = null;
                }
            }
        }
    }
    
    /**
     * Initialize wheat fields for all existing farms on the map
     * Called after loading a map to ensure wheat fields are created for all farms
     * This method is idempotent - it can be called multiple times safely
     * Only creates missing wheat fields, doesn't clear existing valid ones
     */
    initializeWheatFields() {
        // Only create wheat fields around farms - don't clear existing ones
        // The createWheatFieldsAroundFarm method will track which farms created each field
        // in an array, so it's safe to call multiple times
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                const tile = this.map[i][j];
                if (tile === FARM || tile === FARM_OLD) {
                    this.createWheatFieldsAroundFarm(i, j);
                }
            }
        }
        
        // Clean up orphaned wheat fields (no adjacent farms)
        // Do this AFTER creating new ones to avoid race conditions
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                const field = this.wheatFields[i][j];
                if (Array.isArray(field) && field.length > 0) {
                    // Verify that all tracked farms still exist
                    const validFarms = field.filter(farmKey => {
                        const [fi, fj] = farmKey.split(',').map(Number);
                        const tile = this.map[fi][fj];
                        return tile === FARM || tile === FARM_OLD;
                    });
                    
                    // Update or clear the field
                    if (validFarms.length === 0) {
                        this.wheatFields[i][j] = null;
                    } else if (validFarms.length < field.length) {
                        this.wheatFields[i][j] = validFarms;
                    }
                }
            }
        }
    }
    
    /**
     * Get resource value at position (for WOOD, STONE, IRON, FOOD)
     * @param {number} resourceType - The resource type (WOOD, STONE, IRON, FOOD)
     * @param {number} i - Row position
     * @param {number} j - Column position
     * @param {boolean} forOptimization - If true, applies optimization boosts (farm boost, reduced mill boost)
     */
    getResourceValue(resourceType, i, j, forOptimization = false) {
        // Bounds check
        if (i < 0 || i >= 21 || j < 0 || j >= 21) return 0;
        
        const tile = this.map[i][j];
        
        // New buildings (e.g., CUTTER for WOOD)
        if (tile === resourceType + 1) {
            // Get level-based base production
            // Ensure buildingLevel is a valid number between 1-10
            let buildingLevel = this.buildingLevel;
            if (!buildingLevel || buildingLevel < 1 || buildingLevel > 10) {
                buildingLevel = 10; // Default to 10 if invalid
            }
            let base = 0;
            if (resourceType === FOOD) {
                base = getFarmProduction(buildingLevel);
            } else {
                base = getGathererProduction(buildingLevel);
            }
            
            let bonus = 0;
            
            // Special handling for farms: count farm field nodes (OPEN spots adjacent to farm)
            if (resourceType === FOOD) {
                // Count farm field nodes (OPEN spots adjacent to this farm)
                let farmFieldCount = 0;
                for (let di = -1; di <= 1; di++) {
                    for (let dj = -1; dj <= 1; dj++) {
                        if (di === 0 && dj === 0) continue; // Skip self
                        const ni = i + di;
                        const nj = j + dj;
                        if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                            const neighborTile = this.map[ni][nj];
                            // Farm field node: OPEN spot (empty land) adjacent to farm
                            if (neighborTile === OPEN && !this.isLocked(ni, nj)) {
                                farmFieldCount++;
                            }
                        }
                    }
                }
                
                // Farm field bonuses: first field = 50%, subsequent fields = 40% each
                if (farmFieldCount > 0) {
                    const firstFieldBonus = base * 0.5; // First field: +50%
                    const subsequentFieldsBonus = base * 0.4 * (farmFieldCount - 1); // Rest: +40% each
                    bonus = firstFieldBonus + subsequentFieldsBonus;
                }
                
                // Also count lake nodes for additional bonus (separate from farm fields)
                // Lake nodes give a percentage bonus on the value after farm fields
                const lakeNodes = this.countAround(LAKE, i, j);
                if (lakeNodes > 0) {
                    // Each lake node gives 45% bonus
                    const afterFields = base + bonus;
                    const lakeBonusPercent = 45; // 45% per lake node
                    bonus = afterFields + (afterFields * (lakeBonusPercent / 100) * lakeNodes);
                }
            } else {
                // For other resources, use standard resource node bonuses
                // Node bonuses are percentages of base: 45% per node
                const nodeCount = this.countAround(resourceType, i, j);
                const nodeBonusPercent = 45; // Each node gives 45% of base
                bonus = base * (nodeBonusPercent / 100) * nodeCount;
            }
            
            // Start with base + node bonus
            let currentValue = base + bonus;
            
            // For optimization: reduce mill boost for farms to reduce mill count
            // For actual production: use standard refinery bonus based on level
            // Use the validated buildingLevel for refineries too
            // Note: Refineries give a percentage bonus that's ADDITIVE (not multiplicative)
            // Only ONE refinery needs to be adjacent - multiple don't stack
            const refinerBonus = getRefineryBonus(buildingLevel);
            // Reduce mill boost during optimization to discourage over-placing mills
            // Since only one mill is needed per farm cluster, we want fewer mills
            const refinerBonusForCalc = (forOptimization && resourceType === FOOD) ? refinerBonus * 0.6 : refinerBonus;
            // Count refineries: SAWMILL for WOOD, STONEMASON for STONE, FOUNDRY for IRON, MILL for FOOD
            const refineryTile = resourceType === WOOD ? SAWMILL :
                                resourceType === STONE ? STONEMASON :
                                resourceType === IRON ? FOUNDRY : MILL;
            // Refiner bonus: apply as percentage of current value (additive)
            const hasRefiner = this.countAround(refineryTile, i, j) > 0;
            if (hasRefiner) {
                currentValue = currentValue + (currentValue * refinerBonusForCalc);
            }
            
            const nCasas = this.countAround(CASA, i, j);
            // Use the validated buildingLevel for cottage bonuses
            // Casa bonus: apply as percentage of current value (additive)
            const casaBonus = getCottageBonus(buildingLevel);
            if (nCasas > 0) {
                currentValue = currentValue + (currentValue * casaBonus * nCasas);
            }
            
            // Boost for farms to favor them in optimization (only during optimization)
            const farmBoost = (forOptimization && resourceType === FOOD) ? 1.20 : 1.0;
            
            // Apply farm boost (multiplicative, only for optimization)
            const value = currentValue * farmBoost;
            return Math.round(value);
        }
        
        // Old buildings (e.g., CUTTER_OLD)
        if (tile === resourceType + 4) {
            const hasRefiner = this.countAround(resourceType + 2, i, j) > 0;
            const nCasas = this.countAround(CASA, i, j);
            
            let base, bonus, cottageBonus;
            if (resourceType === FOOD) {
                base = 400.0;
                bonus = this.old_food_resource_bonuses[this.countAround(resourceType, i, j)];
                cottageBonus = this.old_food_resource_bonuses[nCottages] * 2;
            } else {
                base = 300.0;
                bonus = this.old_resource_bonuses[this.countAround(resourceType, i, j)];
                cottageBonus = this.old_resource_bonuses[nCottages] * 2;
            }
            
            const bonusMul = 1 + 0.5 * this.countAround(resourceType + 3, i, j);
            const multiplier = hasRefiner ? this.REFINER_MULTIPLIER : 1.0;
            
            // Boost for farms to favor them in optimization (only during optimization)
            const farmBoost = (forOptimization && resourceType === FOOD) ? 1.20 : 1.0;
            
            const value = ((base + bonus + cottageBonus) * bonusMul * multiplier * farmBoost);
            return Math.round(value);
        }
        
        return 0;
    }
    
    /**
     * Get gold value at position
     */
    getGoldValue(i, j) {
        // Bounds check
        if (i < 0 || i >= 21 || j < 0 || j >= 21) return 0;
        
        if (this.map[i][j] === MANOR) {
            const buildingLevel = this.buildingLevel || 10;
            const baseProduction = getEstateProduction(buildingLevel);
            const wharfBonus = getHarborBonus(buildingLevel) * this.countAround(WHARF, i, j);
            const bazaarBonus = getMarketBonus(buildingLevel) * this.countAround(BAZAAR, i, j);
            return baseProduction * (1.0 + wharfBonus + bazaarBonus);
        }
        return 0;
    }
    
    /**
     * Sum resource type across entire map
     */
    sumResourceType(resourceType) {
        let total = 0.0;
        
        if (resourceType === GOLD) {
            for (let i = 1; i < 21; i++) {
                for (let j = 1; j < 21; j++) {
                    total += this.getGoldValue(i, j);
                }
            }
        } else {
            for (let i = 1; i < 21; i++) {
                for (let j = 1; j < 21; j++) {
                    total += this.getResourceValue(resourceType, i, j);
                }
            }
        }
        
        return total;
    }
    
    /**
     * Sum storage for a resource type
     */
    sumStorage(resourceType) {
        let total = this.storage_base;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === CELLARIUM) {
                    // Refinery multiplier (+2 for each refinery)
                    total += this.storage_per_granary * (1 + 2 * this.countAround(resourceType + 2, i, j));
                }
            }
        }
        
        return total;
    }
    
    /**
     * Sum hidden resources (vaults)
     */
    sumHidden(resourceType) {
        let total = 0;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === CACHE) {
                    // +25% per wood resource adjacent
                    total += this.vault_capacity * (1 + 0.25 * this.countAround(WOOD, i, j));
                }
            }
        }
        
        return total;
    }
    
    /**
     * Diagnostic function to log detailed production breakdown
     * Call this from browser console: gameEngine.diagnoseProduction()
     */
    diagnoseProduction() {
        console.log('=== PRODUCTION DIAGNOSTIC ===');
        console.log(`Building Level: ${this.buildingLevel}`);
        console.log(`Keep Level: ${this.basilicaLevel}`);
        
        const buildings = {
            wood: [],
            stone: [],
            iron: [],
            food: []
        };
        
        // Collect all production buildings with their details
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                const tile = this.map[i][j];
                let resourceType = null;
                let production = 0;
                
                if (tile === CUTTER || tile === CUTTER_OLD) {
                    resourceType = WOOD;
                    production = this.getResourceValue(WOOD, i, j);
                } else if (tile === QUARRY || tile === QUARRY_OLD) {
                    resourceType = STONE;
                    production = this.getResourceValue(STONE, i, j);
                } else if (tile === MINE || tile === MINE_OLD) {
                    resourceType = IRON;
                    production = this.getResourceValue(IRON, i, j);
                } else if (tile === FARM || tile === FARM_OLD) {
                    resourceType = FOOD;
                    production = this.getResourceValue(FOOD, i, j);
                }
                
                if (resourceType !== null && production > 0) {
                    const nodeCount = this.countAround(resourceType, i, j);
                    const refinerTile = resourceType === WOOD ? SAWMILL :
                                      resourceType === STONE ? STONEMASON :
                                      resourceType === IRON ? FOUNDRY : MILL;
                    const hasRefiner = this.countAround(refinerTile, i, j) > 0;
                    const casaCount = this.countAround(CASA, i, j);
                    
                    const buildingInfo = {
                        pos: `[${i},${j}]`,
                        production: production,
                        nodeCount: nodeCount,
                        hasRefiner: hasRefiner,
                        casaCount: casaCount
                    };
                    
                    if (resourceType === WOOD) buildings.wood.push(buildingInfo);
                    else if (resourceType === STONE) buildings.stone.push(buildingInfo);
                    else if (resourceType === IRON) buildings.iron.push(buildingInfo);
                    else if (resourceType === FOOD) buildings.food.push(buildingInfo);
                }
            }
        }
        
        // Log summary
        console.log('\n=== TOTALS ===');
        console.log(`Timber: ${this.wood_tot} (buildings: ${buildings.wood.reduce((sum, b) => sum + b.production, 0)}, +${BASILICA_WOOD_PRODUCTION} from Keep)`);
        console.log(`Stone: ${this.stone_tot}`);
        console.log(`Iron: ${this.iron_tot}`);
        console.log(`Grain: ${this.food_tot}`);
        
        // Log individual buildings
        console.log('\n=== WOOD BUILDINGS ===');
        buildings.wood.forEach(b => {
            console.log(`${b.pos}: ${b.production} (nodes: ${b.nodeCount}, refiner: ${b.hasRefiner}, casas: ${b.casaCount})`);
        });
        
        console.log('\n=== STONE BUILDINGS ===');
        buildings.stone.forEach(b => {
            console.log(`${b.pos}: ${b.production} (nodes: ${b.nodeCount}, refiner: ${b.hasRefiner}, casas: ${b.casaCount})`);
        });
        
        console.log('\n=== IRON BUILDINGS ===');
        buildings.iron.forEach(b => {
            console.log(`${b.pos}: ${b.production} (nodes: ${b.nodeCount}, refiner: ${b.hasRefiner}, casas: ${b.casaCount})`);
        });
        
        console.log('\n=== FOOD BUILDINGS ===');
        buildings.food.forEach(b => {
            console.log(`${b.pos}: ${b.production} (nodes: ${b.nodeCount}, refiner: ${b.hasRefiner}, casas: ${b.casaCount})`);
        });
        
        return {
            totals: {
                wood: this.wood_tot,
                stone: this.stone_tot,
                iron: this.iron_tot,
                food: this.food_tot
            },
            buildings: buildings
        };
    }
    
    /**
     * Update all resource totals
     */
    updateTotals() {
        // Keep wood production (constant at all levels, but use constant for clarity)
        // Note: BASILICA_WOOD_PRODUCTION is constant 300, but we use it for clarity
        this.wood_tot = this.sumResourceType(WOOD) + BASILICA_WOOD_PRODUCTION;
        this.stone_tot = this.sumResourceType(STONE);
        this.iron_tot = this.sumResourceType(IRON);
        this.food_tot = this.sumResourceType(FOOD);
        this.gold_tot = this.sumResourceType(GOLD);
        
        // Update max buildings based on Basilica level
        const basilicaLevel = this.basilicaLevel || 10;
        this.maxBuildings = getKeepBuildingSlots(basilicaLevel);
        
        this.wood_storage = this.sumStorage(WOOD);
        this.stone_storage = this.sumStorage(STONE);
        this.iron_storage = this.sumStorage(IRON);
        this.food_storage = this.sumStorage(FOOD);
        
        this.wood_hidden = this.sumHidden(WOOD);
        this.stone_hidden = this.sumHidden(STONE);
        this.iron_hidden = this.sumHidden(IRON);
        this.food_hidden = this.sumHidden(FOOD);
        
        // Military building counts
        this.guard_tot = this.countBuildings(CITY_GUARD_HOUSE);
        this.training_ground_tot = this.countBuildings(TRAINING_GROUND);
        this.stable_tot = this.countBuildings(STABLES);
        this.moonglow_tot = this.countBuildings(MOONGLOW);
        this.workshop_tot = this.countBuildings(WORKSHOP);
        this.shipyard_tot = this.countBuildings(SHIPYARD);
        this.temple_tot = this.countBuildings(TEMPLE);
        
        // Enlistment speed bonuses based on building level
        // Pattern: +5%, +10%, +16%, +24%, +35%, +50%, +68%, +90%, +115%, +150%
        const enlistmentSpeedBonuses = [0.05, 0.10, 0.16, 0.24, 0.35, 0.50, 0.68, 0.90, 1.15, 1.50];
        
        // Ensure buildingLevel is a valid number between 1 and 10
        let buildingLevel = parseInt(this.buildingLevel);
        if (isNaN(buildingLevel) || buildingLevel < 1 || buildingLevel > 10) {
            buildingLevel = 10; // Default to level 10 if invalid
        }
        
        const levelBonus = enlistmentSpeedBonuses[buildingLevel - 1] || 1.50;
        const levelMultiplier = 1.0 + levelBonus; // e.g., level 1 = 1.05, level 10 = 2.50
        
        // Barracks enlistment speed bonuses by level: +1%, +2%, +4%, +6%, +8%, +10%, +13%, +16%, +20%, +25%
        const barracksEnlistmentBonuses = [0.01, 0.02, 0.04, 0.06, 0.08, 0.10, 0.13, 0.16, 0.20, 0.25];
        const barracksBonus = barracksEnlistmentBonuses[buildingLevel - 1] || 0.25;
        
        // Compute rates with level-based bonuses
        // Base contribution per building = enlistment speed bonus (e.g., 0.05 for level 1, 1.50 for level 10)
        // Barracks bonus: each adjacent barracks adds its percentage bonus to the base
        const computeRate = (buildingType) => {
            let rate = 1.0;
            const baseEnlistmentSpeed = enlistmentSpeedBonuses[buildingLevel - 1] || 1.50;
            for (let i = 1; i < 21; i++) {
                for (let j = 1; j < 21; j++) {
                    if (this.map[i][j] === buildingType) {
                        // Base contribution = enlistment speed bonus for this level
                        // Barracks bonus: each adjacent barracks adds its percentage bonus
                        // Formula: baseEnlistmentSpeed + (baseEnlistmentSpeed * barracksBonus * adjacentBarracks)
                        const adjacentBarracks = this.countAround(BARRACKS, i, j);
                        const barracksBonusAmount = baseEnlistmentSpeed * barracksBonus * adjacentBarracks;
                        const contribution = baseEnlistmentSpeed + barracksBonusAmount;
                        rate += contribution;
                    }
                }
            }
            return rate;
        };
        
        // Guard rate: each guardhouse contributes its enlistment speed bonus
        // Guards don't benefit from barracks bonuses
        const guardEnlistmentSpeed = enlistmentSpeedBonuses[buildingLevel - 1] || 1.50;
        const guardBaseRate = (this.countBuildings(CITY_GUARD_HOUSE) * guardEnlistmentSpeed) + 1.0;
        this.guard_rate = (this.guard_tot > 0 ? 1 : 0) * guardBaseRate;
        
        // Other military building rates
        this.training_ground_rate = (this.training_ground_tot > 0 ? 1 : 0) * computeRate(TRAINING_GROUND);
        this.stable_rate = (this.stable_tot > 0 ? 1 : 0) * computeRate(STABLES);
        this.moonglow_rate = (this.moonglow_tot > 0 ? 1 : 0) * computeRate(MOONGLOW);
        this.workshop_rate = (this.workshop_tot > 0 ? 1 : 0) * computeRate(WORKSHOP);
        this.shipyard_rate = (this.shipyard_tot > 0 ? 1 : 0) * computeRate(SHIPYARD);
        this.temple_rate = (this.temple_tot > 0 ? 1 : 0) * computeRate(TEMPLE);
        
        // Calculate troop capacity based on barracks and castle levels
        // Barracks Army Size by level: 10, 30, 60, 100, 160, 240, 350, 500, 700, 1000
        const barracksArmySizes = [10, 30, 60, 100, 160, 240, 350, 500, 700, 1000];
        let totalBarracksCapacity = 0;
        
        // Sum up capacity from all barracks based on their level
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === BARRACKS) {
                    const barracksLevel = buildingLevel; // Use global building level
                    const armySize = barracksArmySizes[barracksLevel - 1] || 1000;
                    totalBarracksCapacity += armySize;
                }
            }
        }
        
        // Castle Army Size bonus by level: +20%, +40%, +60%, +80%, +100%, +130%, +160%, +200%, +250%, +300%
        const castleBonuses = [0.20, 0.40, 0.60, 0.80, 1.00, 1.30, 1.60, 2.00, 2.50, 3.00];
        let castleMultiplier = 1.0;
        
        // Check if there's a castle and apply its bonus
        if (this.countBuildings(CASTLE) > 0) {
            const castleLevel = buildingLevel; // Use global building level
            const castleBonus = castleBonuses[castleLevel - 1] || 3.00;
            castleMultiplier = 1.0 + castleBonus; // e.g., level 1 = 1.20, level 10 = 4.00
        }
        
        this.troop_capacity_tot = totalBarracksCapacity * castleMultiplier;
        this.casa_tot = this.countBuildings(CASA);
        
        // Calculate building rate based on casa levels
        // Construction speed bonuses from buildings.md: +4%, +10%, +16%, +25%, +35%, +46%, +58%, +71%, +85%, +100%
        const constructionSpeedBonuses = [0.04, 0.10, 0.16, 0.25, 0.35, 0.46, 0.58, 0.71, 0.85, 1.0];
        let totalCasaBonus = 0.0;
        // buildingLevel is already declared above for military rates
        
        // Sum up construction speed bonuses from all casas
        // Each casa contributes its level's construction speed bonus
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === CASA) {
                    const casaLevel = buildingLevel; // Use global building level for all casas
                    const bonus = constructionSpeedBonuses[casaLevel - 1] || 1.0;
                    totalCasaBonus += bonus;
                }
            }
        }
        
        // Base rate is 1.0 (100%), plus sum of all casa bonuses
        this.building_rate = 1.0 + totalCasaBonus;
        this.building_tot = this.countAllBuildings();
        
        this.cart_tot = this.countBuildings(BAZAAR) * 200;
        this.ship_tot = this.countBuildings(WHARF) * 30;
        
        this.producer_tot = 
            this.countBuildings(CUTTER) + this.countBuildings(CUTTER_OLD) +
            this.countBuildings(QUARRY) + this.countBuildings(QUARRY_OLD) +
            this.countBuildings(MINE) + this.countBuildings(MINE_OLD) +
            this.countBuildings(FARM) + this.countBuildings(FARM_OLD) +
            this.countBuildings(MANOR);
        
        this.refiner_tot =
            this.countBuildings(SAWMILL) +
            this.countBuildings(STONEMASON) +
            this.countBuildings(FOUNDRY) +
            this.countBuildings(MILL) +
            this.countBuildings(WHARF) +
            this.countBuildings(BAZAAR);
    }
    
    /**
     * Clear the entire map to template (land or water)
     * @param {boolean} isWater - Whether to use water template (true) or land template (false)
     */
    clearMapToTemplate(isWater = false) {
        const template = isWater ? this.waterMap : this.landMap;
        this.map = this.createMapFromTemplate(template);
        this.copyMapToOrigMap();
        // Also clear locks and wheat fields
        this.locks = this.createEmptyMap();
        this.wheatFields = this.createEmptyMap();
    }
    
    /**
     * Clear resources
     */
    clearResources() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (isResource(this.map[i][j])) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear buildings
     */
    clearBuildings() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (isBuilding(this.map[i][j])) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear non-lakes
     */
    clearNonLakes() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                const tile = this.map[i][j];
                if (isBuilding(tile) || (isResource(tile) && tile !== LAKE)) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear cabins
     */
    clearCabins() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === CASA) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear lakes
     */
    clearLakes() {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] === LAKE) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear resources by quadrant
     */
    clearResourcesByQuadrant(quadrant) {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (ZONE[i][j] === quadrant && isResource(this.map[i][j])) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear buildings by quadrant
     */
    clearBuildingsByQuadrant(quadrant) {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (ZONE[i][j] === quadrant && isBuilding(this.map[i][j])) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear non-lakes by quadrant
     */
    clearNonLakesByQuadrant(quadrant) {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (ZONE[i][j] === quadrant) {
                    const tile = this.map[i][j];
                    if (isBuilding(tile) || (isResource(tile) && tile !== LAKE)) {
                        this.placeByChar('`', i, j);
                    }
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear cabins by quadrant
     */
    clearCabinsByQuadrant(quadrant) {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (ZONE[i][j] === quadrant && this.map[i][j] === CASA) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Clear lakes by quadrant
     */
    clearLakesByQuadrant(quadrant) {
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (ZONE[i][j] === quadrant && this.map[i][j] === LAKE) {
                    this.placeByChar('`', i, j);
                }
            }
        }
        this.updateTotals();
    }
    
    /**
     * Get helper building value (CELLARIUM, CACHE, BARRACKS, CASA)
     * These buildings contribute to city efficiency indirectly
     * Values are small bonuses - these are supporting buildings, not primary producers
     */
    getHelperBuildingValue(i, j) {
        const tile = this.map[i][j];
        let value = 0;
        
        // MILL: Direct penalty to reduce mill count during optimization
        // Mills boost farms, but we want fewer of them
        if (tile === MILL) {
            // Count nearby farms to see how much this mill would boost
            const farmCount = this.countAround(FARM, i, j);
            // Base penalty to make mills less attractive (fine-tuned to reduce by 1)
            const basePenalty = -320;
            // Small value for boosting farms, but not enough to overcome penalty
            const boostValue = farmCount * 37;
            value = basePenalty + boostValue;
        }
        
        // CELLARIUM: Storage bonus - value increases with nearby refiners
        // Small bonus per refiner (storage is useful but not primary production)
        if (tile === CELLARIUM) {
            // Count refiners around (SAWMILL, STONEMASON, FOUNDRY, MILL)
            const refinerCount = this.countAroundMask(0x000f, 0x0002, i, j);
            // Small bonus: 5 points per refiner (scaled by average weight)
            const avgWeight = (this.wood_weight + this.stone_weight + this.iron_weight + this.food_weight) / 4;
            value = refinerCount * 5 * avgWeight;
        }
        
        // CACHE: Hidden storage - value increases with nearby wood resources
        // Small bonus per wood resource
        if (tile === CACHE) {
            const woodCount = this.countAround(WOOD, i, j);
            // Small bonus: 2 points per wood resource
            const avgWeight = (this.wood_weight + this.stone_weight + this.iron_weight + this.food_weight) / 4;
            value = woodCount * 2 * avgWeight;
        }
        
        // BARRACKS: Military capacity - value based on nearby military buildings
        // Very small bonus - military buildings don't produce resources
        if (tile === BARRACKS) {
            const militaryCount = this.countMilitaryAround(i, j);
            // Small bonus: 1 point base + 0.5 per nearby military building
            // Military is important but not for resource optimization
            value = 1 + (militaryCount * 0.5);
        }
        
        // CASA: Construction speed and production bonus
        // Casas provide:
        // 1. Construction speed bonus (global, not position-dependent)
        // 2. Production bonus to adjacent producers (based on level, stacks per casa)
        // Direct penalty to reduce casa count during optimization
        if (tile === CASA) {
            // Count nearby PRODUCERS (not refiners) that benefit from casa production bonus
            // Casas give production bonus to: CUTTER, QUARRY, MINE, FARM, MANOR
            const producerCount = this.countAroundMask(0x000f, 0x0001, i, j);
            // Base penalty to make casas less attractive
            // Since casas give stacking production bonuses, we want them near multiple producers
            const basePenalty = -180;
            // Bonus per producer - cabins give significant production boost (30% per cabin at level 10)
            // The more producers nearby, the more valuable the cabin
            const avgWeight = (this.wood_weight + this.stone_weight + this.iron_weight + this.food_weight) / 4;
            // Increased bonus per producer since cabin bonuses are significant and stack
            const bonusValue = producerCount * 0.35 * avgWeight;
            value = basePenalty + bonusValue;
        }
        
        // FOUNDRY (Smelter): Value increases with nearby iron mines
        // Smelters boost iron mine production, so they should be near iron mines
        // Use a strong gradient to encourage movement toward mines
        if (tile === FOUNDRY) {
            const ironMineCount = this.countAround(MINE, i, j);
            // Base value - smelters are useful but much more valuable near mines
            const baseValue = 10 * this.iron_weight;
            // Large bonus per adjacent mine
            const adjacentBonus = ironMineCount * 200 * this.iron_weight;
            
            // Also add proximity bonus for mines within 2 tiles (encourages movement toward mines)
            // This ensures moving one step closer shows improvement even if not directly adjacent
            let proximityBonus = 0;
            for (let di = -2; di <= 2; di++) {
                for (let dj = -2; dj <= 2; dj++) {
                    if (di === 0 && dj === 0) continue; // Skip self
                    const ni = i + di;
                    const nj = j + dj;
                    if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                        if (this.map[ni][nj] === MINE) {
                            // Distance-based bonus: closer mines give more value
                            const distance = Math.max(Math.abs(di), Math.abs(dj)); // Chebyshev distance
                            if (distance === 1) {
                                // Already counted in adjacentBonus, skip to avoid double-counting
                                continue;
                            } else if (distance === 2) {
                                // Mines 2 steps away give smaller bonus to encourage movement
                                proximityBonus += 30 * this.iron_weight;
                            }
                        }
                    }
                }
            }
            
            value = baseValue + adjacentBonus + proximityBonus;
        }
        
        // SAWMILL: Value increases with nearby foresters (CUTTER)
        // Sawmills boost forester production, so they should be near foresters
        // Use a strong gradient to encourage movement toward foresters
        if (tile === SAWMILL) {
            const foresterCount = this.countAround(CUTTER, i, j);
            // Base value - sawmills are useful but much more valuable near foresters
            const baseValue = 10 * this.wood_weight;
            // Large bonus per forester - ensures movement toward foresters shows improvement
            const bonusValue = foresterCount * 200 * this.wood_weight;
            value = baseValue + bonusValue;
        }
        
        // STONEMASON: Value increases with nearby stone mines (QUARRY)
        // Stonemasons boost stone mine production, so they should be near stone mines
        // Use a strong gradient to encourage movement toward mines
        if (tile === STONEMASON) {
            const stoneMineCount = this.countAround(QUARRY, i, j);
            // Base value - stonemasons are useful but much more valuable near mines
            const baseValue = 10 * this.stone_weight;
            // Large bonus per mine - ensures movement toward mines shows improvement
            const bonusValue = stoneMineCount * 200 * this.stone_weight;
            value = baseValue + bonusValue;
        }
        
        return value;
    }
    
    /**
     * Evaluation functions for optimization
     */
    eval1x1(i, j) {
        // Use forOptimization=true to apply farm boost and reduced mill boost during optimization
        const resourceValue = (
            (this.wood_weight !== 0.0 ? this.getResourceValue(WOOD, i, j, true) * this.wood_weight : 0.0) +
            (this.stone_weight !== 0.0 ? this.getResourceValue(STONE, i, j, true) * this.stone_weight : 0.0) +
            (this.iron_weight !== 0.0 ? this.getResourceValue(IRON, i, j, true) * this.iron_weight : 0.0) +
            (this.food_weight !== 0.0 ? this.getResourceValue(FOOD, i, j, true) * this.food_weight : 0.0) +
            (this.gold_weight !== 0.0 ? this.getGoldValue(i, j) * this.gold_weight : 0.0)
        );
        
        // Add helper building value
        const helperValue = this.getHelperBuildingValue(i, j);
        
        return resourceValue + helperValue;
    }
    
    eval3x3Plain(i, j) {
        // Allow evaluation up to the edges (i=0 to i=20, j=0 to j=20)
        // but only evaluate positions that are within bounds (0-20)
        let total = 0;
        
        for (let di = -1; di <= 1; di++) {
            for (let dj = -1; dj <= 1; dj++) {
                const ni = i + di;
                const nj = j + dj;
                // Only evaluate if within map bounds (0-20)
                if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                    total += this.eval1x1(ni, nj);
                }
            }
        }
        
        return total;
    }
    
    /**
     * Push refiners apart - penalize clustering of refiners
     * Refiners don't benefit from being close together, so we penalize placing them too close
     */
    pushRefineriesApart(i, j) {
        const tile = this.map[i][j];
        // Check if this is a refiner (bit 0x0002 in the lower 4 bits)
        if ((tile & 0x000f) === 0x0002) {
            let penalty = 0;
            
            // Extra penalty for mills to reduce mill count
            const isMill = (tile === MILL);
            if (isMill) {
                // Base penalty for mills (fine-tuned to reduce by 1)
                penalty -= 320;
            }
            
            // Penalty if next to a wall
            if (this.countAround(WALL, i, j) > 0) {
                penalty -= 25;
            }
            
            // Penalty for each other refiner of the same type in a 5x5 area
            // This includes itself, so we subtract 1 to get the count of OTHER refiners
            const sameTypeCount = this.countAround2(tile, i, j);
            // Extra penalty for mills clustering
            const clusteringPenalty = isMill ? 150 : 100;
            penalty -= clusteringPenalty * sameTypeCount;
            
            return penalty;
        }
        
        return 0;
    }
    
    /**
     * Resource clustering bonus - favor placing same resource types together
     * This helps create efficient resource clusters
     * Updated to better understand bonuses: resource nodes give flat bonuses, refiners give multipliers
     */
    getResourceClusteringBonus(i, j) {
        const tile = this.map[i][j];
        let bonus = 0;
        
        // Only apply to resource-producing buildings (not refiners, not helpers)
        if ((tile & 0x000f) === 0x0001) { // Producer (CUTTER, QUARRY, MINE, FARM, MANOR)
            // Get resource type
            const resourceType = tile & 0xff00;
            
            // Special handling for farms: prioritize OPEN spots (farm field nodes)
            if (tile === FARM || tile === FARM_OLD) {
                // Count OPEN spots around (farm field nodes) - these give 50%/40% bonuses
                let openSpots = 0;
                for (let di = -1; di <= 1; di++) {
                    for (let dj = -1; dj <= 1; dj++) {
                        if (di === 0 && dj === 0) continue;
                        const ni = i + di;
                        const nj = j + dj;
                        if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                            const neighborTile = this.map[ni][nj];
                            if (neighborTile === OPEN && !this.isLocked(ni, nj)) {
                                openSpots++;
                            }
                        }
                    }
                }
                // Significant bonus for OPEN spots - first gives 50%, rest give 40% each
                // This is a major production boost, so weight it heavily
                if (openSpots > 0) {
                    const firstFieldBonus = 50; // First field = 50% bonus
                    const subsequentFieldsBonus = 40 * (openSpots - 1); // Rest = 40% each
                    bonus += (firstFieldBonus + subsequentFieldsBonus) * 0.8; // Scale to optimization weights
                }
                
                // Also count lake nodes (45% bonus per node on value after farm fields)
                const lakeNodes = this.countAround(LAKE, i, j);
                if (lakeNodes > 0) {
                    bonus += lakeNodes * 45 * 0.6; // 45% per node, scaled
                }
            } else {
                // For other producers, count resource nodes (they give flat bonuses)
                const sameResourceCount = this.countAround(resourceType, i, j);
                // Resource nodes give significant flat bonuses (150, 270, 390, etc.)
                // So clustering near resource nodes is very valuable
                bonus += sameResourceCount * 8; // Increased from 5
            }
            
            // Count same building type around (clustering)
            const sameBuildingCount = this.countAround(tile, i, j);
            bonus += sameBuildingCount * 3;
            
            // Avoid stomping other resources
            const otherResourceCount = this.countAroundMask(0xff00, 0xff00, i, j) - 
                                     (tile === FARM || tile === FARM_OLD ? 0 : this.countAround(resourceType, i, j));
            bonus -= otherResourceCount * 2;
        }
        
        // Gold buildings (MANOR) benefit from clustering
        if (tile === MANOR) {
            const manorCount = this.countAround(MANOR, i, j);
            bonus += manorCount * 3;
        }
        
        return bonus;
    }
    
    eval3x3(i, j) {
        return this.eval3x3Plain(i, j) + 
               this.pushRefineriesApart(i, j) + 
               this.getResourceClusteringBonus(i, j);
    }
    
    /**
     * ShareString conversion functions
     */
    tileToShareString(tile) {
        for (const k in this.keyBindings) {
            if (this.keyBindings[k][0] === tile) {
                return this.keyBindings[k][4];
            }
        }
        return '#';
    }
    
    tileToLinkChar(tile) {
        for (const k in this.keyBindings) {
            if (this.keyBindings[k][0] === tile) {
                return this.keyBindings[k][3];
            }
        }
        return '0';
    }
    
    shareStringCharToChar(ch) {
        for (const k in this.keyBindings) {
            if (this.keyBindings[k][4] === ch) {
                return k;
            }
        }
        console.warn(`Invalid ShareString character: ${ch}`);
        return ' ';
    }
    
    linkCharToChar(ch) {
        for (const k in this.keyBindings) {
            if (this.keyBindings[k][3] === ch) {
                return k;
            }
        }
        console.warn(`Invalid link character: ${ch}`);
        return ' ';
    }
    
    /**
     * Export ShareString format
     */
    exportShareString() {
        let ret = `[ShareString.1.3]${this.water ? ';' : ':'}\n`;
        
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                ret += this.tileToShareString(this.map[i][j]);
            }
            ret += '\n';
        }
        
        ret += '[/ShareString]\n';
        return ret;
    }
    
    /**
     * Export ShareLink format
     */
    exportShareLink() {
        let ret = this.water ? 'W' : 'L';
        
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                if (this.map[i][j] !== WALL) {
                    ret += this.tileToLinkChar(this.map[i][j]);
                }
            }
        }
        
        return ret;
    }
    
    /**
     * Load from ShareString
     * @param {string} str - The ShareString to load
     * @param {boolean} clearMap - Whether to clear the map before loading
     * @param {boolean|null} overrideWater - Override terrain type: true for water, false for land, null to use ShareString's value
     */
    loadFromShareString(str, clearMap = true, overrideWater = null) {
        let i = 0, j = 0;
        str = str.replace(/\s/g, '');
        
        // Check if this is actually a ShareLink format (starts with W or L)
        const firstChar = str.charAt(0);
        if (firstChar === 'W' || firstChar === 'w' || firstChar === 'L' || firstChar === 'l') {
            // This is a ShareLink, not a ShareString - route to the correct handler
            console.log('Detected ShareLink format in loadFromShareString, routing to loadFromLink');
            return this.loadFromLink(str, clearMap, overrideWater);
        }
        
        // Clear map first if requested
        if (clearMap) {
            // Determine water mode early to clear to correct template
            let willBeWater = false;
            if (overrideWater !== null) {
                willBeWater = overrideWater;
            } else {
                // Read from ShareString
                if (str.charAt(0) === ';') {
                    willBeWater = true;
                } else if (str.charAt(0) === ':') {
                    willBeWater = false;
                }
            }
            this.clearMapToTemplate(willBeWater);
        }
        
        // More flexible regex to handle URL-encoded brackets or missing closing tag
        // Use non-greedy match and make closing tag optional
        const match = /\[*ShareString\.1\.3\]([^[]*?)(\[\/ShareString\]|$)/.exec(str);
        if (!match) {
            console.error("Can't handle that version of ShareString. Try ShareString 1.3");
            console.error("ShareString received (first 100 chars):", str.substring(0, 100));
            return;
        }
        
        str = match[1];
        
        // Validate that we have enough data (should be 441 chars for 21x21 grid, plus the first char for water/land)
        if (str.length < 442) {
            console.warn(`ShareString data length is ${str.length}, expected at least 442 characters (21x21 grid + water/land indicator)`);
        }
        
        // Set water mode - use override if provided, otherwise read from ShareString
        if (overrideWater !== null) {
            this.water = overrideWater;
        } else {
            // Read from ShareString
            if (str.charAt(0) === ';') {
                this.water = true;
            } else if (str.charAt(0) === ':') {
                this.water = false;
            } else {
                console.warn(`ShareString first character is '${str.charAt(0)}', expected ';' (water) or ':' (land). Defaulting to land.`);
                this.water = false;
            }
        }
        this.updateWaterTiles(this.water);
        
        // Load map - start from index 1 to skip the water/land indicator
        for (let idx = 1; idx < str.length; idx++) {
            const t = str.charAt(idx).replace(/\s/, '');
            if (t.length === 0) continue;
            
            if (this.map[i][j] > DEEP_WATER) {
                const ch = this.shareStringCharToChar(t);
                if (clearMap || (ch !== 'open' && ch !== 'water')) {
                    this.placeByChar(ch, i, j);
                }
            }
            
            j++;
            if (j >= 21) {
                j = 0;
                i++;
            }
            if (i >= 21) {
                break;
            }
        }
        
        this.copyMapToOrigMap();
        
        // Patch deep water tiles if needed
        if (this.water) {
            for (i = 0; i < 21; i++) {
                for (j = 0; j < 21; j++) {
                    if (this.map[i][j] === WALL && this.waterMap[i][j] !== WALL) {
                        this.map[i][j] = this.waterMap[i][j];
                    }
                }
            }
        }
        
        // Initialize wheat fields for all existing farms
        this.initializeWheatFields();
        
        this.updateTotals();
    }
    
    /**
     * Load from ShareLink
     * @param {string} str - The ShareLink to load
     * @param {boolean} clearMap - Whether to clear the map before loading
     * @param {boolean|null} overrideWater - Override terrain type: true for water, false for land, null to use link's value
     */
    loadFromLink(str, clearMap = true, overrideWater = null) {
        // Extract link if it has = in it
        if (str.includes('=')) {
            str = str.split('=')[1].trim();
        }
        
        // Trim any whitespace that might have been introduced during URL encoding/decoding
        str = str.trim();
        
        // Determine expected map length (294 for land, 287 for water)
        // New format may include resources/troops appended, so we need to extract just the map portion
        // Check first character to determine water/land
        const firstChar = str.charAt(0);
        const isWater = firstChar === 'W' || firstChar === 'w';
        const expectedMapLength = isWater ? 287 : 294;
        
        // Clear map first if requested
        if (clearMap) {
            // Determine water mode early to clear to correct template
            const willBeWater = overrideWater !== null ? overrideWater : isWater;
            this.clearMapToTemplate(willBeWater);
        }
        
        // Debug logging for URL encoding issues
        if (str.length !== expectedMapLength && str.length < expectedMapLength + 10) {
            console.log(`loadFromLink: length=${str.length}, firstChar='${firstChar}', isWater=${isWater}, expected=${expectedMapLength}`);
            console.log(`loadFromLink: full string: "${str}"`);
        }
        
        // Handle strings that are shorter or longer than expected
        // Check if there's a ShortCode appended (4 characters after expected length)
        let mapString;
        let appendedShortCode = null;
        
        if (str.length < expectedMapLength) {
            // String is shorter than expected - use what we have (may be incomplete data)
            console.warn(`Link string is shorter than expected (${str.length} vs ${expectedMapLength} for ${isWater ? 'water' : 'land'}). Loading with available characters.`);
            mapString = str; // Use the entire string, even if incomplete
        } else if (str.length === expectedMapLength + 4) {
            // String is exactly 4 characters longer - likely has ShortCode appended
            mapString = str.substring(0, expectedMapLength);
            appendedShortCode = str.substring(expectedMapLength);
            // Validate that the appended part looks like a ShortCode (alphanumeric, 4 chars)
            if (!/^[A-Za-z0-9]{4}$/.test(appendedShortCode)) {
                // Doesn't look like a ShortCode, treat as part of map data
                mapString = str.substring(0, expectedMapLength);
                appendedShortCode = null;
            } else {
                console.log(`Detected appended ShortCode: ${appendedShortCode}`);
            }
        } else if (str.length > expectedMapLength) {
            // String is longer than expected - extract only the map portion
            // This handles cases where old data might have resources/troops appended directly to LongLink
            mapString = str.substring(0, expectedMapLength);
            console.warn(`Link string is longer than expected (${str.length} vs ${expectedMapLength}). Extracting only map portion. Extra characters: ${str.length - expectedMapLength}`);
        } else {
            // Perfect length
            mapString = str;
        }
        
        // Store appended ShortCode for later use (if any)
        if (appendedShortCode) {
            this.appendedShortCode = appendedShortCode;
        }
        
        let i = 0, j = 0;
        
        // Set water mode - use override if provided, otherwise read from ShareLink
        if (overrideWater !== null) {
            this.water = overrideWater;
            this.updateWaterTiles(this.water);
        } else {
            // Read from ShareLink
            if (mapString.charAt(0) === 'W' || mapString.charAt(0) === 'w') {
                this.water = true;
            } else {
                this.water = false;
            }
            this.updateWaterTiles(this.water);
        }
        
        // Load map
        for (let idx = 1; idx < mapString.length; idx++) {
            j++;
            // Skip walls and deep water
            while (this.map[i][j] === WALL || (mapString.length === 287 && this.map[i][j] === DEEP_WATER)) {
                j++;
                if (j >= 21) {
                    j = 0;
                    i++;
                }
            }
            
            const ch = this.linkCharToChar(mapString.charAt(idx));
            if (clearMap || (ch !== 'open' && ch !== 'water')) {
                this.placeByChar(ch, i, j);
            }
        }
        
        this.copyMapToOrigMap();
        
        // Initialize wheat fields for all existing farms
        this.initializeWheatFields();
        
        this.updateTotals();
    }
    
    /**
     * Load from string (auto-detect format)
     */
    /**
     * Load from string (ShareString or ShareLink)
     * @param {string} str - The string to load
     * @param {boolean} clearMap - Whether to clear the map before loading
     * @param {boolean|null} overrideWater - Override terrain type: true for water, false for land, null to use string's value
     */
    loadFromString(str, clearMap = true, overrideWater = null) {
        this.last_load_string = str;
        
        // Check if this is a ShareString format (contains [ShareString)
        if (str.includes('ShareString') || str.includes('[ShareString')) {
            this.loadFromShareString(str, clearMap, overrideWater);
        } else {
            // Otherwise, treat as ShareLink format
            this.loadFromLink(str, clearMap, overrideWater);
        }
        this.resetLocks();
    }
    
    /**
     * Stash and restore weights for optimization
     */
    stashAndClearWeightsExceptFor(resourceType) {
        const ret = [
            this.wood_weight,
            this.stone_weight,
            this.iron_weight,
            this.food_weight,
            this.gold_weight
        ];
        
        this.wood_weight = 0.0;
        this.stone_weight = 0.0;
        this.iron_weight = 0.0;
        this.food_weight = 0.0;
        this.gold_weight = 0.0;
        
        if (resourceType === WOOD) {
            this.wood_weight = 1.0;
        } else if (resourceType === STONE) {
            this.stone_weight = 1.0;
        } else if (resourceType === IRON) {
            this.iron_weight = 1.0;
        } else if (resourceType === FOOD) {
            this.food_weight = 1.0;
        } else if (resourceType === GOLD) {
            this.gold_weight = 1.0;
        } else {
            this.restoreWeights(ret);
        }
        
        return ret;
    }
    
    restoreWeights(arr) {
        this.wood_weight = arr[0];
        this.stone_weight = arr[1];
        this.iron_weight = arr[2];
        this.food_weight = arr[3];
        this.gold_weight = arr[4];
    }
    
    /**
     * Get optimal tile from tileset for position
     */
    getOptimalTileFromTileset(tileset, i, j) {
        const origTile = this.map[i][j];
        let best = { tile: origTile, value: this.eval3x3(i, j), i, j };
        const origValue = best.value;
        
        // Get the base map terrain for this position
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        // DEEP_WATER tiles cannot have any buildings placed on them
        if (baseMap[i][j] === DEEP_WATER) {
            return { tile: origTile, value: -100000, i, j };
        }
        
        const baseTerrain = baseMap[i][j] & TERRAIN_TYPE;
        
        // Skip if this is a water-only tile and we're not in water mode, or vice versa
        // (This prevents evaluating invalid positions)
        if (baseTerrain === 0) {
            // No terrain type set - invalid position
            return { tile: origTile, value: 0, i, j };
        }
        
        for (let idx = 0; idx < tileset.length; idx++) {
            const tileOption = tileset[idx];
            const tileTerrain = tileOption & TERRAIN_TYPE;
            
            // Check terrain compatibility - use base map to ensure correct terrain
            // Both must have the same terrain type (both water or both land)
            // Also check: water buildings can only go on water, land buildings can only go on land
            if (baseTerrain === tileTerrain && tileTerrain !== 0) {
                // Additional check: if base terrain is water, only allow water buildings
                // (WHARF, SHIPYARD have IS_WATER flag)
                if (baseTerrain === IS_WATER && (tileOption & IS_WATER) === 0) {
                    continue; // Trying to place land building on water - skip
                }
                // If base terrain is land, only allow land buildings
                if (baseTerrain === IS_LAND && (tileOption & IS_WATER) !== 0) {
                    continue; // Trying to place water building on land - skip (shouldn't happen but be safe)
                }
                
                this.map[i][j] = tileOption;
                const v = this.eval3x3(i, j);
                if (v > best.value) {
                    best.tile = tileOption;
                    best.value = v;
                }
            }
        }
        
        // Restore original tile
        this.map[i][j] = origTile;
        
        // Return delta value (gain from placing this tile)
        best.value -= origValue;
        
        return best;
    }
    
    /**
     * Get optimal tile for resource type at position
     */
    getOptimalTile(resourceType, i, j) {
        // Get base map for terrain checking
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        // DEEP_WATER tiles cannot have any buildings placed on them (including WHARF/SHIPYARD)
        if (baseMap[i][j] === DEEP_WATER) {
            return { tile: this.map[i][j], value: -100000, i, j };
        }
        
        const baseTerrain = baseMap[i][j] & TERRAIN_TYPE;
        
        // Skip water tiles for resource types that don't include water buildings
        // (unless the resource type is GOLD or ANY which include WHARF)
        if (baseTerrain === IS_WATER && resourceType !== GOLD && resourceType !== ANY) {
            return { tile: this.map[i][j], value: -100000, i, j };
        }
        
        // Additional safeguard: if on water and resourceType is ANY, we need to ensure
        // only water buildings are considered (getOptimalTileFromTileset will handle this)
        
        // Get tileset - include original tile
        let tileset = [this.origMap[i][j]];
        if (this.tilesets[resourceType]) {
            tileset = tileset.concat(this.tilesets[resourceType]);
        }
        
        return this.getOptimalTileFromTileset(tileset, i, j);
    }
    
    /**
     * Add a building optimally (internal, returns TileValue)
     */
    __add(resourceType) {
        this.message = "";
        let best = { tile: 0, value: 0, i: 0, j: 0 };
        
        const stash = this.stashAndClearWeightsExceptFor(resourceType);
        
        // Get base map for terrain checking
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.map[i][j] <= DEEP_WATER) continue;
                if (this.isLocked(i, j)) continue;
                
                // DEEP_WATER tiles cannot have any buildings placed on them (including WHARF/SHIPYARD)
                if (baseMap[i][j] === DEEP_WATER) {
                    continue;
                }
                
                // Check terrain compatibility - skip water tiles for non-water resource types
                const baseTerrain = baseMap[i][j] & TERRAIN_TYPE;
                if (baseTerrain === IS_WATER) {
                    // On water tiles, only allow water buildings (WHARF, SHIPYARD)
                    // Skip if resource type doesn't include water buildings
                    if (resourceType !== GOLD && resourceType !== ANY) {
                        continue; // Land-only resource types (WOOD, STONE, IRON, FOOD) cannot be placed on water
                    }
                    // For GOLD and ANY, getOptimalTile will filter out land buildings
                } else if (baseTerrain === IS_LAND) {
                    // On land tiles, only allow land buildings
                    // Skip if resource type is GOLD-only (which only has WHARF/BAZAAR/MANOR)
                    // Actually, GOLD includes MANOR which is land, so allow it
                    // But if we're on land and resourceType is GOLD, we should still evaluate
                    // (MANOR can go on land, WHARF can't)
                }
                
                const tv = this.getOptimalTile(resourceType, i, j);
                if (tv.value > best.value) {
                    best = tv;
                }
            }
        }
        
        if (best.i && best.j) {
            const keyChar = this.tileToChar(best.tile);
            if (keyChar) {
                this.placeByChar(keyChar, best.i, best.j, false); // Don't save undo in optimization - caller handles it
            }
        } else {
            this.message = `Couldn't find a good place to add a ${resourceType === WOOD ? 'wood' : resourceType === STONE ? 'stone' : resourceType === IRON ? 'iron' : resourceType === FOOD ? 'food' : 'gold'} building`;
        }
        
        this.restoreWeights(stash);
        
        return best;
    }
    
    /**
     * Add building (public API)
     */
    add(resourceType) {
        try {
            const ret = this.__add(resourceType);
            this.updateTotals();
            return ret.value || 0.0;
        } catch (err) {
            console.error('Error in add:', err);
            this.message = `Error: ${err.message}`;
            return 0.0;
        }
    }
    
    /**
     * Remove a building optimally (internal, returns TileValue)
     */
    __remove(resourceType) {
        this.message = "";
        let best = { tile: 0, value: -100000, i: 0, j: 0 };
        
        const stash = this.stashAndClearWeightsExceptFor(resourceType);
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.isLocked(i, j)) continue;
                if (!isBuilding(this.map[i][j])) continue;
                if (resourceType === CASA && this.map[i][j] !== CASA) continue;
                
                const t = this.map[i][j];
                const a = this.eval3x3Plain(i, j);
                
                // Calculate what it would be if removed
                const TERRAIN_TYPE = 0x0300;
                const IS_WATER = 0x0100;
                if ((this.origMap[i][j] & 0x000f) === 0 || this.origMap[i][j] === LAKE) {
                    this.map[i][j] = this.origMap[i][j];
                } else if (this.map[i][j] & IS_WATER) {
                    this.map[i][j] = WATER;
                } else {
                    this.map[i][j] = OPEN;
                }
                
                const b = this.eval3x3Plain(i, j);
                
                // Only remove if it affects the resource type OR if it's a special case
                // Helper buildings (CELLARIUM, CACHE, BARRACKS) can always be removed during optimization
                // as they affect overall city efficiency
                const ANY = 0x8000;
                if ((a - b) !== 0 ||
                    ((resourceType === GOLD || resourceType === ANY) && (t === WHARF || t === BAZAAR)) ||
                    ((resourceType === CASA || resourceType === ANY) && t === CASA) ||
                    (resourceType === ANY && (t === CELLARIUM || t === CACHE || t === BARRACKS))) {
                    if ((b - a) > best.value) {
                        best.value = b - a;
                        best.tile = this.map[i][j];
                        best.i = i;
                        best.j = j;
                    }
                }
                
                // Restore
                this.map[i][j] = t;
            }
        }
        
        if (best.i && best.j) {
            const keyChar = this.tileToChar(best.tile);
            if (keyChar) {
                this.placeByChar(keyChar, best.i, best.j, false); // Don't save undo in optimization - caller handles it
            }
        } else {
            this.message = "Ran out of removable buildings";
        }
        
        this.restoreWeights(stash);
        
        return best;
    }
    
    /**
     * Remove building (public API)
     */
    remove(resourceType) {
        try {
            const ret = this.__remove(resourceType);
            this.updateTotals();
            return ret.value || 0.0;
        } catch (err) {
            console.error('Error in remove:', err);
            this.message = `Error: ${err.message}`;
            return 0.0;
        }
    }
    
    /**
     * Helper: Convert tile to character
     */
    tileToChar(tile) {
        for (const k in this.keyBindings) {
            if (this.keyBindings[k][0] === tile) {
                return k;
            }
        }
        return null;
    }
    
    /**
     * Count military units around a position
     */
    countMilitaryAround(i, j) {
        // Check bounds for each neighbor position to handle edge cases
        let count = 0;
        
        // Check all 8 neighbors, only counting if they're within bounds
        for (let di = -1; di <= 1; di++) {
            for (let dj = -1; dj <= 1; dj++) {
                if (di === 0 && dj === 0) continue; // Skip center tile
                const ni = i + di;
                const nj = j + dj;
                if (ni >= 0 && ni < 21 && nj >= 0 && nj < 21) {
                    if (isMilitary(this.map[ni][nj])) {
                        count++;
                    }
                }
            }
        }
        
        return count;
    }
    
    /**
     * Get heat map value for a tile
     * Returns { val: weighted value, touches: adjacent building count }
     */
    getHeatMapValue(i, j) {
        // Allow evaluation up to the last row/column (indices 1-20)
        if (i < 1 || i >= 21 || j < 1 || j >= 21) {
            return { val: 0, touches: 0 };
        }
        
        const tile = this.map[i][j];
        let touches = 0;
        
        // Calculate touches based on building type
        if (tile === CELLARIUM) {
            // Count refiners around (mask 0x000f, eq 0x0002)
            touches = this.countAroundMask(0x000f, 0x0002, i, j);
        } else if (tile === CACHE) {
            // Count WOOD resources around
            touches = this.countAround(WOOD, i, j);
        } else if (isMilitary(tile)) {
            // Count BARRACKS around
            touches = this.countAround(BARRACKS, i, j);
        } else if (tile === BARRACKS) {
            // Count military units around
            touches = this.countMilitaryAround(i, j);
        } else if ((tile & 0xf) === 0x2) {
            // Refiner (SAWMILL, STONEMASON, FOUNDRY, MILL)
            touches = this.countAround(CELLARIUM, i, j);
        }
        
        // Calculate weighted value
        const val = this.eval1x1(i, j);
        
        return { val, touches };
    }
    
    /**
     * Update heat map data for all tiles
     */
    updateHeatMap() {
        // Heat map is calculated on-the-fly when rendering
        // This method can be used for caching if needed
    }
    
    /**
     * Fine-tune optimization - iteratively improve building placements
     * Swaps out suboptimal buildings and performs add/remove cycles
     */
    fineTune(maxIterations = 50) {
        let iterations = 0;
        let improvements = 0;
        
        // First pass: replace any buildings that are suboptimal
        // Collect all candidates first, then sort by improvement potential
        const candidates = [];
        
        // Get base map for terrain checking
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.isLocked(i, j)) continue;
                
                // Skip DEEP_WATER - no buildings can be placed there
                if (baseMap[i][j] === DEEP_WATER) continue;
                
                const currentTile = this.map[i][j];
                if (!isBuilding(currentTile)) continue;
                
                // Get optimal tile for this position
                const optimal = this.getOptimalTile(ANY, i, j);
                
                // Verify the optimal tile is terrain-compatible
                const baseTerrain = baseMap[i][j] & TERRAIN_TYPE;
                if (baseTerrain === IS_WATER) {
                    // On water, only water buildings are allowed
                    if ((optimal.tile & IS_WATER) === 0) {
                        continue; // Optimal tile is a land building on water - skip
                    }
                } else if (baseTerrain === IS_LAND) {
                    // On land, only land buildings are allowed
                    if ((optimal.tile & IS_WATER) !== 0) {
                        continue; // Optimal tile is a water building on land - skip
                    }
                }
                
                // optimal.value is the delta (gain from placing), so if it's positive and different tile, replace
                if (optimal.value > 0 && optimal.tile !== currentTile) {
                    candidates.push({
                        i, j,
                        currentTile,
                        optimalTile: optimal.tile,
                        improvement: optimal.value
                    });
                }
            }
        }
        
        // Sort by improvement potential (highest first)
        candidates.sort((a, b) => b.improvement - a.improvement);
        
        // Apply improvements in order of highest impact
        for (const candidate of candidates) {
            const keyChar = this.tileToChar(candidate.optimalTile);
            if (keyChar) {
                this.placeByChar(keyChar, candidate.i, candidate.j, false);
                improvements++;
            }
        }
        
        if (improvements > 0) {
            this.updateTotals();
        }
        
        // Second pass: iterative add/remove cycles
        while (iterations < maxIterations) {
            const removed = this.__remove(ANY);
            const added = this.__add(ANY);
            
            // If we removed and added the same tile at the same position, we're done
            if (removed.i === added.i && removed.j === added.j && removed.tile === added.tile) {
                break;
            }
            
            iterations++;
        }
        
        return improvements;
    }
    
    /**
     * Fine-tune optimization by quadrant - iteratively improve building placements in a specific quadrant
     * @param {number} quadrant - Quadrant number (1-5)
     * @param {number} maxIterations - Maximum iterations for add/remove cycles
     */
    fineTuneByQuadrant(quadrant, maxIterations = 50) {
        let iterations = 0;
        let improvements = 0;
        
        // First pass: replace any buildings that are suboptimal
        // Collect all candidates first, then sort by improvement potential
        const candidates = [];
        
        // Get base map for terrain checking
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                // Only process tiles in the specified quadrant
                if (ZONE[i][j] !== quadrant) continue;
                
                if (this.isLocked(i, j)) continue;
                
                // Skip DEEP_WATER - no buildings can be placed there
                if (baseMap[i][j] === DEEP_WATER) continue;
                
                const currentTile = this.map[i][j];
                if (!isBuilding(currentTile)) continue;
                
                // Get optimal tile for this position
                const optimal = this.getOptimalTile(ANY, i, j);
                
                // Verify the optimal tile is terrain-compatible
                const baseTerrain = baseMap[i][j] & TERRAIN_TYPE;
                if (baseTerrain === IS_WATER) {
                    // On water, only water buildings are allowed
                    if ((optimal.tile & IS_WATER) === 0) {
                        continue; // Optimal tile is a land building on water - skip
                    }
                } else if (baseTerrain === IS_LAND) {
                    // On land, only land buildings are allowed
                    if ((optimal.tile & IS_WATER) !== 0) {
                        continue; // Optimal tile is a water building on land - skip
                    }
                }
                
                // optimal.value is the delta (gain from placing), so if it's positive and different tile, replace
                if (optimal.value > 0 && optimal.tile !== currentTile) {
                    candidates.push({
                        i, j,
                        currentTile,
                        optimalTile: optimal.tile,
                        improvement: optimal.value
                    });
                }
            }
        }
        
        // Sort by improvement potential (highest first)
        candidates.sort((a, b) => b.improvement - a.improvement);
        
        // Apply improvements in order of highest impact
        for (const candidate of candidates) {
            const keyChar = this.tileToChar(candidate.optimalTile);
            if (keyChar) {
                this.placeByChar(keyChar, candidate.i, candidate.j, false);
                improvements++;
            }
        }
        
        if (improvements > 0) {
            this.updateTotals();
        }
        
        // Second pass: iterative add/remove cycles (quadrant-specific)
        while (iterations < maxIterations) {
            const removed = this.__removeByQuadrant(ANY, quadrant);
            const added = this.__addByQuadrant(ANY, quadrant);
            
            // If we removed and added the same tile at the same position, we're done
            if (removed.i === added.i && removed.j === added.j && removed.tile === added.tile) {
                break;
            }
            
            iterations++;
        }
        
        return improvements;
    }
    
    /**
     * Remove negative resource nodes - remove resource nodes that have negative value
     */
    removeNegativeResourceNodes() {
        let removed = 0;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.isLocked(i, j)) continue;
                
                const tile = this.map[i][j];
                if (!isRealResource(tile)) continue;
                
                // Calculate value of this tile
                const value = this.eval1x1(i, j);
                
                // If negative value, remove it
                if (value < 0) {
                    const baseMap = this.water ? this.waterMap : this.landMap;
                    this.map[i][j] = (baseMap[i][j] & IS_LAND) ? OPEN : WATER;
                    removed++;
                }
            }
        }
        
        if (removed > 0) {
            this.updateTotals();
        }
        
        return removed;
    }
    
    /**
     * Remove negative resource nodes by quadrant
     * @param {number} quadrant - Quadrant number (1-5)
     */
    removeNegativeResourceNodesByQuadrant(quadrant) {
        let removed = 0;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                // Only process tiles in the specified quadrant
                if (ZONE[i][j] !== quadrant) continue;
                
                if (this.isLocked(i, j)) continue;
                
                const tile = this.map[i][j];
                if (!isRealResource(tile)) continue;
                
                // Calculate value of this tile
                const value = this.eval1x1(i, j);
                
                // If negative value, remove it
                if (value < 0) {
                    const baseMap = this.water ? this.waterMap : this.landMap;
                    this.map[i][j] = (baseMap[i][j] & IS_LAND) ? OPEN : WATER;
                    removed++;
                }
            }
        }
        
        if (removed > 0) {
            this.updateTotals();
        }
        
        return removed;
    }
    
    /**
     * Swap adjacent buildings if it improves the overall value
     * Checks horizontal, vertical, and diagonal neighbors
     * Evaluates 5x5 area for better context
     */
    swapBuildings() {
        let swaps = 0;
        const swapCandidates = [];
        
        // Collect all potential swaps first
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (this.isLocked(i, j)) continue;
                
                const baseMap = this.water ? this.waterMap : this.landMap;
                const tile1 = this.map[i][j];
                
                // Skip if not a building or on water
                if (!isBuilding(tile1) || (baseMap[i][j] & IS_WATER)) continue;
                
                // Check all 8 neighbors (horizontal, vertical, diagonal)
                const neighbors = [
                    { di: 0, dj: 1 },   // right
                    { di: 1, dj: 0 },   // bottom
                    { di: 1, dj: 1 },   // bottom-right
                    { di: 1, dj: -1 }   // bottom-left
                ];
                
                for (const { di, dj } of neighbors) {
                    const ni = i + di;
                    const nj = j + dj;
                    
                    if (ni < 1 || ni >= 21 || nj < 1 || nj >= 21) continue;
                    if (this.isLocked(ni, nj)) continue;
                    if (baseMap[ni][nj] & IS_WATER) continue;
                    
                    const tile2 = this.map[ni][nj];
                    if (!isBuilding(tile2)) continue;
                    
                    // Evaluate 5x5 area for better context
                    let valueBefore = 0;
                    let valueAfter = 0;
                    
                    // Calculate before value (5x5 area)
                    for (let di2 = -2; di2 <= 2; di2++) {
                        for (let dj2 = -2; dj2 <= 2; dj2++) {
                            const ai = i + di2;
                            const aj = j + dj2;
                            const bi = ni + di2;
                            const bj = nj + dj2;
                            
                            if (ai >= 0 && ai < 21 && aj >= 0 && aj < 21) {
                                valueBefore += this.eval1x1(ai, aj);
                            }
                            if (bi >= 0 && bi < 21 && bj >= 0 && bj < 21) {
                                valueBefore += this.eval1x1(bi, bj);
                            }
                        }
                    }
                    
                    // Try swapping
                    this.map[i][j] = tile2;
                    this.map[ni][nj] = tile1;
                    
                    // Calculate after value (5x5 area)
                    for (let di2 = -2; di2 <= 2; di2++) {
                        for (let dj2 = -2; dj2 <= 2; dj2++) {
                            const ai = i + di2;
                            const aj = j + dj2;
                            const bi = ni + di2;
                            const bj = nj + dj2;
                            
                            if (ai >= 0 && ai < 21 && aj >= 0 && aj < 21) {
                                valueAfter += this.eval1x1(ai, aj);
                            }
                            if (bi >= 0 && bi < 21 && bj >= 0 && bj < 21) {
                                valueAfter += this.eval1x1(bi, bj);
                            }
                        }
                    }
                    
                    const improvement = valueAfter - valueBefore;
                    
                    // If not better, swap back
                    if (improvement <= 0) {
                        this.map[i][j] = tile1;
                        this.map[ni][nj] = tile2;
                    } else {
                        // Store candidate for later (avoid double-counting)
                        swapCandidates.push({
                            i, j, ni, nj,
                            improvement
                        });
                        // Swap back temporarily - we'll apply best swaps later
                        this.map[i][j] = tile1;
                        this.map[ni][nj] = tile2;
                    }
                }
            }
        }
        
        // Sort by improvement and apply best swaps (avoid overlapping)
        swapCandidates.sort((a, b) => b.improvement - a.improvement);
        const swapped = new Set();
        
        for (const candidate of swapCandidates) {
            const key1 = `${candidate.i},${candidate.j}`;
            const key2 = `${candidate.ni},${candidate.nj}`;
            
            // Skip if either position already swapped
            if (swapped.has(key1) || swapped.has(key2)) continue;
            
            // Apply swap
            const tile1 = this.map[candidate.i][candidate.j];
            const tile2 = this.map[candidate.ni][candidate.nj];
            this.map[candidate.i][candidate.j] = tile2;
            this.map[candidate.ni][candidate.nj] = tile1;
            
            swapped.add(key1);
            swapped.add(key2);
            swaps++;
        }
        
        if (swaps > 0) {
            this.updateTotals();
        }
        
        return swaps;
    }
    
    /**
     * Move buildings to optimal positions across the entire map
     * Finds the best position for each building and moves it there (not just adjacent swaps)
     * @param {number|null} quadrant - Quadrant number (1-5) to optimize, or null for entire map
     * @returns {number} Number of moves made
     */
    moveBuildingsToOptimalPositions(quadrant = null) {
        let moves = 0;
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        // Collect all buildings with their current positions
        const buildings = [];
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                if (quadrant && ZONE[i][j] !== quadrant) continue;
                if (this.isLocked(i, j)) continue;
                if (baseMap[i][j] & IS_WATER) continue;
                
                const tile = this.map[i][j];
                if (!isBuilding(tile)) continue;
                
                // Get current value of this building at its position
                const currentValue = this.eval1x1(i, j);
                buildings.push({ tile, i, j, currentValue });
            }
        }
        
        // Sort by current value (lowest first) - optimize worst-placed buildings first
        buildings.sort((a, b) => a.currentValue - b.currentValue);
        
        const moved = new Set();
        
        // For each building, find its optimal position
        for (const building of buildings) {
            const key = `${building.i},${building.j}`;
            if (moved.has(key)) continue; // Already moved
            
            let bestPosition = { i: building.i, j: building.j, value: building.currentValue };
            
            // Search entire map for best position for this building
            for (let ni = 1; ni < 21; ni++) {
                for (let nj = 1; nj < 21; nj++) {
                    if (quadrant && ZONE[ni][nj] !== quadrant) continue;
                    if (this.isLocked(ni, nj)) continue;
                    if (baseMap[ni][nj] & IS_WATER) continue;
                    
                    const nKey = `${ni},${nj}`;
                    if (moved.has(nKey)) continue; // Position already taken by moved building
                    
                    // Check terrain compatibility
                    const baseTerrain = baseMap[ni][nj] & TERRAIN_TYPE;
                    const tileTerrain = building.tile & TERRAIN_TYPE;
                    if (baseTerrain !== tileTerrain) continue;
                    
                    // Temporarily place building at this position
                    const originalTile = this.map[ni][nj];
                    this.map[ni][nj] = building.tile;
                    
                    // Evaluate value at this position
                    const value = this.eval1x1(ni, nj);
                    
                    // If better, remember this position
                    if (value > bestPosition.value) {
                        bestPosition = { i: ni, j: nj, value };
                    }
                    
                    // Restore original tile
                    this.map[ni][nj] = originalTile;
                }
            }
            
            // If we found a better position, move the building there
            if (bestPosition.i !== building.i || bestPosition.j !== building.j) {
                const targetKey = `${bestPosition.i},${bestPosition.j}`;
                if (!moved.has(targetKey)) {
                    // Swap the buildings
                    const targetTile = this.map[bestPosition.i][bestPosition.j];
                    this.map[building.i][building.j] = targetTile;
                    this.map[bestPosition.i][bestPosition.j] = building.tile;
                    
                    moved.add(key);
                    moved.add(targetKey);
                    moves++;
                }
            }
        }
        
        if (moves > 0) {
            this.updateTotals();
        }
        
        return moves;
    }
    
    /**
     * Move buildings to optimal positions by quadrant
     * @param {number} quadrant - Quadrant number (1-5)
     * @returns {number} Number of moves made
     */
    moveBuildingsToOptimalPositionsByQuadrant(quadrant) {
        return this.moveBuildingsToOptimalPositions(quadrant);
    }
    
    /**
     * Swap buildings by quadrant for better arrangements
     * @param {number} quadrant - Quadrant number (1-5)
     * @returns {number} Number of swaps made
     */
    swapBuildingsByQuadrant(quadrant) {
        let swaps = 0;
        const swapCandidates = [];
        
        // Collect all potential swaps first (only in specified quadrant)
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                // Only process tiles in the specified quadrant
                if (ZONE[i][j] !== quadrant) continue;
                
                if (this.isLocked(i, j)) continue;
                
                const baseMap = this.water ? this.waterMap : this.landMap;
                const tile1 = this.map[i][j];
                
                // Skip if not a building or on water
                if (!isBuilding(tile1) || (baseMap[i][j] & IS_WATER)) continue;
                
                // Check all 8 neighbors (horizontal, vertical, diagonal)
                const neighbors = [
                    { di: 0, dj: 1 },   // right
                    { di: 1, dj: 0 },   // bottom
                    { di: 1, dj: 1 },   // bottom-right
                    { di: 1, dj: -1 }   // bottom-left
                ];
                
                for (const { di, dj } of neighbors) {
                    const ni = i + di;
                    const nj = j + dj;
                    
                    if (ni < 1 || ni >= 21 || nj < 1 || nj >= 21) continue;
                    // Only swap with neighbors in the same quadrant
                    if (ZONE[ni][nj] !== quadrant) continue;
                    if (this.isLocked(ni, nj)) continue;
                    if (baseMap[ni][nj] & IS_WATER) continue;
                    
                    const tile2 = this.map[ni][nj];
                    if (!isBuilding(tile2)) continue;
                    
                    // Evaluate 5x5 area for better context
                    let valueBefore = 0;
                    let valueAfter = 0;
                    
                    // Calculate before value (5x5 area)
                    for (let di2 = -2; di2 <= 2; di2++) {
                        for (let dj2 = -2; dj2 <= 2; dj2++) {
                            const ai = i + di2;
                            const aj = j + dj2;
                            const bi = ni + di2;
                            const bj = nj + dj2;
                            
                            if (ai >= 0 && ai < 21 && aj >= 0 && aj < 21) {
                                valueBefore += this.eval1x1(ai, aj);
                            }
                            if (bi >= 0 && bi < 21 && bj >= 0 && bj < 21) {
                                valueBefore += this.eval1x1(bi, bj);
                            }
                        }
                    }
                    
                    // Try swapping
                    this.map[i][j] = tile2;
                    this.map[ni][nj] = tile1;
                    
                    // Calculate after value (5x5 area)
                    for (let di2 = -2; di2 <= 2; di2++) {
                        for (let dj2 = -2; dj2 <= 2; dj2++) {
                            const ai = i + di2;
                            const aj = j + dj2;
                            const bi = ni + di2;
                            const bj = nj + dj2;
                            
                            if (ai >= 0 && ai < 21 && aj >= 0 && aj < 21) {
                                valueAfter += this.eval1x1(ai, aj);
                            }
                            if (bi >= 0 && bi < 21 && bj >= 0 && bj < 21) {
                                valueAfter += this.eval1x1(bi, bj);
                            }
                        }
                    }
                    
                    const improvement = valueAfter - valueBefore;
                    
                    // If not better, swap back
                    if (improvement <= 0) {
                        this.map[i][j] = tile1;
                        this.map[ni][nj] = tile2;
                    } else {
                        // Store candidate for later (avoid double-counting)
                        swapCandidates.push({
                            i, j, ni, nj,
                            improvement
                        });
                        // Swap back temporarily - we'll apply best swaps later
                        this.map[i][j] = tile1;
                        this.map[ni][nj] = tile2;
                    }
                }
            }
        }
        
        // Sort by improvement and apply best swaps (avoid overlapping)
        swapCandidates.sort((a, b) => b.improvement - a.improvement);
        
        const used = new Set();
        for (const candidate of swapCandidates) {
            const key1 = `${candidate.i},${candidate.j}`;
            const key2 = `${candidate.ni},${candidate.nj}`;
            
            if (used.has(key1) || used.has(key2)) continue;
            
            // Apply swap
            const tile1 = this.map[candidate.i][candidate.j];
            const tile2 = this.map[candidate.ni][candidate.nj];
            this.map[candidate.i][candidate.j] = tile2;
            this.map[candidate.ni][candidate.nj] = tile1;
            
            used.add(key1);
            used.add(key2);
            swaps++;
        }
        
        if (swaps > 0) {
            this.updateTotals();
        }
        
        return swaps;
    }
    
    /**
     * Add building by quadrant (internal, returns TileValue)
     * @param {number} resourceType - Resource type to optimize for
     * @param {number} quadrant - Quadrant number (1-5)
     */
    __addByQuadrant(resourceType, quadrant) {
        this.message = "";
        let best = { tile: 0, value: 0, i: 0, j: 0 };
        
        const stash = this.stashAndClearWeightsExceptFor(resourceType);
        
        // Get base map for terrain checking
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                // Only process tiles in the specified quadrant
                if (ZONE[i][j] !== quadrant) continue;
                
                if (this.map[i][j] <= DEEP_WATER) continue;
                if (this.isLocked(i, j)) continue;
                
                // DEEP_WATER tiles cannot have any buildings placed on them (including WHARF/SHIPYARD)
                if (baseMap[i][j] === DEEP_WATER) {
                    continue;
                }
                
                // Check terrain compatibility - skip water tiles for non-water resource types
                const baseTerrain = baseMap[i][j] & TERRAIN_TYPE;
                if (baseTerrain === IS_WATER) {
                    // On water tiles, only allow water buildings (WHARF, SHIPYARD)
                    // Skip if resource type doesn't include water buildings
                    if (resourceType !== GOLD && resourceType !== ANY) {
                        continue; // Land-only resource types (WOOD, STONE, IRON, FOOD) cannot be placed on water
                    }
                    // For GOLD and ANY, getOptimalTile will filter out land buildings
                } else if (baseTerrain === IS_LAND) {
                    // On land tiles, only allow land buildings
                    // Skip if resource type is GOLD-only (which only has WHARF/BAZAAR/MANOR)
                    // Actually, GOLD includes MANOR which is land, so allow it
                    // But if we're on land and resourceType is GOLD, we should still evaluate
                    // (MANOR can go on land, WHARF can't)
                }
                
                const tv = this.getOptimalTile(resourceType, i, j);
                if (tv.value > best.value) {
                    best = tv;
                }
            }
        }
        
        if (best.i && best.j) {
            const keyChar = this.tileToChar(best.tile);
            if (keyChar) {
                this.placeByChar(keyChar, best.i, best.j, false); // Don't save undo in optimization - caller handles it
            }
        } else {
            this.message = `Couldn't find a good place to add a ${resourceType === WOOD ? 'wood' : resourceType === STONE ? 'stone' : resourceType === IRON ? 'iron' : resourceType === FOOD ? 'food' : 'gold'} building`;
        }
        
        this.restoreWeights(stash);
        
        return best;
    }
    
    /**
     * Remove building by quadrant (internal, returns TileValue)
     * @param {number} resourceType - Resource type to optimize for
     * @param {number} quadrant - Quadrant number (1-5)
     */
    __removeByQuadrant(resourceType, quadrant) {
        this.message = "";
        let best = { tile: 0, value: -100000, i: 0, j: 0 };
        
        const stash = this.stashAndClearWeightsExceptFor(resourceType);
        
        // Get base map for terrain checking
        const baseMap = this.water ? this.waterMap : this.landMap;
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                // Only process tiles in the specified quadrant
                if (ZONE[i][j] !== quadrant) continue;
                
                if (this.isLocked(i, j)) continue;
                if (!isBuilding(this.map[i][j])) continue;
                if (resourceType === CASA && this.map[i][j] !== CASA) continue;
                
                const t = this.map[i][j];
                const a = this.eval3x3Plain(i, j);
                
                // Calculate what it would be if removed
                if ((this.origMap[i][j] & 0x000f) === 0 || this.origMap[i][j] === LAKE) {
                    this.map[i][j] = this.origMap[i][j];
                } else if (this.map[i][j] & IS_WATER) {
                    this.map[i][j] = WATER;
                } else {
                    this.map[i][j] = OPEN;
                }
                
                const b = this.eval3x3Plain(i, j);
                
                // Only remove if it affects the resource type OR if it's a special case
                // Helper buildings (CELLARIUM, CACHE, BARRACKS) can always be removed during optimization
                // as they affect overall city efficiency
                if ((a - b) !== 0 ||
                    ((resourceType === GOLD || resourceType === ANY) && (t === WHARF || t === BAZAAR)) ||
                    ((resourceType === CASA || resourceType === ANY) && t === CASA) ||
                    (resourceType === ANY && (t === CELLARIUM || t === CACHE || t === BARRACKS))) {
                    if ((b - a) > best.value) {
                        best.value = b - a;
                        best.tile = this.map[i][j];
                        best.i = i;
                        best.j = j;
                    }
                }
                
                // Restore
                this.map[i][j] = t;
            }
        }
        
        if (best.i && best.j) {
            const keyChar = this.tileToChar(best.tile);
            if (keyChar) {
                this.placeByChar(keyChar, best.i, best.j, false); // Don't save undo in optimization - caller handles it
            }
        } else {
            this.message = "Ran out of removable buildings";
        }
        
        this.restoreWeights(stash);
        
        return best;
    }
}
